import { type interfaces, Container } from 'inversify';
import WebRTCIssueDetector, {
  type IssueDetectorResult,
  type NetworkScores,
  type CompositeStatsParser,
  AvailableOutgoingBitrateIssueDetector,
  CompositeRTCStatsParser,
  FrozenVideoTrackDetector,
  InboundNetworkIssueDetector,
  MosQuality,
  NetworkMediaSyncIssueDetector,
  NetworkScoresCalculator,
  OutboundNetworkIssueDetector,
  PeriodicWebRTCStatsReporter,
  QualityLimitationsIssueDetector,
  RTCStatsParser,
  UnknownVideoDecoderImplementationDetector,
  VideoDecoderIssueDetector,
  WebRTCIssueEmitter,
} from 'webrtc-issue-detector';
import type ConfigService from './config/ConfigService';
import { HALF_SECOND_IN_MS, MEDIASOUP_SSRC_PROBATOR, TWO_SECONDS_IN_MS } from './constants/common';
import { ClientMetaProvider } from './ClientMetaProvider';
import Engine from './engine';
import AnalyticsApiClient from './engine/analyticsApiClient';
import { CallState } from './engine/CallState';
import ChannelStateSynchronizer from './engine/ChannelStateSynchronizer/ChannelStateSynchronizer';
import EventsQueue from './engine/EventsQueue';
import ChannelAudioObserverEventHandler from './engine/handlers/ChannelAudioObserverEventHandler';
import ChannelAudioObserverMissingProducerRestorer
  from './engine/handlers/ChannelAudioObserverMissingProducerRestorer';
import ChannelEventHandler from './engine/handlers/ChannelEventHandler';
import ChannelStateSyncEventHandler from './engine/handlers/ChannelStateSyncEventHandler';
import MediaEventHandler from './engine/handlers/MediaEventHandler';
import { IntegrationsService } from './engine/IntegrationsService';
import LocalMutex from './engine/LocalMutex';
import { MessageBatcher } from 'message-batcher';
import Logger, { BATCH_SIZE } from './engine/Logger';
import Media from './engine/media';
import PendingClosureProducerStorage from './engine/media/producer/PendingClosureProducerStorage';
import ProducerCloseManager from './engine/media/producer/ProducerCloseManager';
import { ProcessorsCache } from './engine/media/streamEffects/ProcessorsCache';
import MediaStreamTrackManager from './engine/media/tracks/MediaStreamTrackManager';
import { MyPeer } from './engine/MyPeer';
import Network from './engine/network';
import DataChannelsManager from './engine/network/DataChannelsManager';
import DatachannelHeartbeater from './engine/network/DatachannelHeartbeater';
import SignalingHeartbeater from './engine/network/SignalingHeartbeater';
import { Peers } from './engine/Peers';
import PeersStorage from './engine/PeersStorage';
import { RemotePeerTracks } from './engine/RemotePeerTracks';
import SignalingApiClient from './engine/signalingApiClient/SignalingApiClient';
import System from './engine/system';
import StatsHandler from './engine/WebRTCStats/StatsHandler';
import TransportsStatsProvider from './engine/WebRTCStats/TransportsStatsProvider';
import WIDHandler from './engine/WIDHandler';
import NetworkTransportPolicyManager from './engine/network/NetworkTransportPolicyManager';
import {
  audioTrackFactory,
  audioTrackProcessorFactory,
  peerConsumerFactory,
  peerFactory,
  peerTrackFactory,
  videoTrackFactory,
} from './inversify.factories';
import { TOKEN } from './inversify.tokens';
import type {
  AudioTrackFactory,
  AudioTrackProcessorFactory,
  LoggerFactory,
  PeerConsumerFactory,
  PeerFactory,
  PeerTrackFactory,
  VideoTrackFactory,
} from './types/container';
import { ScoresCalculator } from './qoe/ScoresCalculator';
import { WIDStatsAdapter } from './qoe/WIDStatsAdapter';

export const container = new Container({
  autoBindInjectable: true,
});

container
  .bind<ClientMetaProvider>(TOKEN.ClientMetaProvider)
  .to(ClientMetaProvider)
  .inSingletonScope();

container
  .bind<LocalMutex>(TOKEN.LocalMutex)
  .to(LocalMutex)
  .inSingletonScope();

container
  .bind<SignalingApiClient>(TOKEN.SignalingApiClient)
  .toDynamicValue((ctx): SignalingApiClient => new SignalingApiClient(ctx.container.get<ConfigService>(TOKEN.Config)))
  .inSingletonScope();

container
  .bind<CallState>(TOKEN.CallState)
  .to(CallState)
  .inSingletonScope();

container
  .bind<MyPeer>(TOKEN.MyPeer)
  .to(MyPeer)
  .inSingletonScope();

container
  .bind<Logger>(TOKEN.LoggerFactory)
  .toFactory((context: interfaces.Context): LoggerFactory => {
    const config = context.container.get<ConfigService>(TOKEN.Config);

    const logLevel = config.get('logLevel');
    const sendAnalytics = config.get('sendAnalytics');
    const onLogMessage = config.get('onLogMessage');
    const clientMetaProvider = context.container.get<ClientMetaProvider>(TOKEN.ClientMetaProvider);
    const analyticsApiClient = context.container.get<AnalyticsApiClient>(TOKEN.AnalyticsApiClient);

    const logToRemoteBatcher = new MessageBatcher({
      MaxBatchSize: BATCH_SIZE,
      MaxDelay: TWO_SECONDS_IN_MS,
      MinDelay: HALF_SECOND_IN_MS,
    });

    logToRemoteBatcher.on('batch', (logs) => {
      void analyticsApiClient.log.batch({ logs, clientMeta: clientMetaProvider.meta });
    });

    return (namespace: string) => new Logger({
      logLevel,
      namespace,
      onLogMessage,
      sendAnalytics,
      logToRemoteBatcher,
    });
  });

container
  .bind<System>(TOKEN.System)
  .to(System)
  .inSingletonScope();

container
  .bind<ProcessorsCache>(TOKEN.ProcessorsCache)
  .to(ProcessorsCache)
  .inSingletonScope();

container
  .bind<IntegrationsService>(TOKEN.IntegrationsService)
  .to(IntegrationsService)
  .inSingletonScope();

container
  .bind<MediaStreamTrackManager>(TOKEN.MediaStreamTrackManager)
  .to(MediaStreamTrackManager)
  .inSingletonScope();

container
  .bind<Media>(TOKEN.Media)
  .to(Media)
  .inSingletonScope();

container
  .bind<Network>(TOKEN.Network)
  .to(Network)
  .inSingletonScope();

container
  .bind<NetworkTransportPolicyManager>(TOKEN.NetworkTransportPolicyManager)
  .to(NetworkTransportPolicyManager)
  .inSingletonScope();

container
  .bind<PendingClosureProducerStorage>(TOKEN.PendingClosureProducerStorage)
  .to(PendingClosureProducerStorage)
  .inSingletonScope();

container
  .bind<ProducerCloseManager>(TOKEN.ProducerCloseManager)
  .to(ProducerCloseManager)
  .inSingletonScope();

container
  .bind<RemotePeerTracks>(TOKEN.RemotePeerTracks)
  .to(RemotePeerTracks)
  .inSingletonScope();

container
  .bind<Peers>(TOKEN.Peers)
  .to(Peers)
  .inSingletonScope();

container
  .bind<MediaEventHandler>(TOKEN.MediaEventHandler)
  .to(MediaEventHandler)
  .inSingletonScope();

container
  .bind<ChannelEventHandler>(TOKEN.ChannelEventHandler)
  .to(ChannelEventHandler)
  .inSingletonScope();

container
  .bind<ChannelAudioObserverEventHandler>(TOKEN.ChannelAudioObserverEventHandler)
  .to(ChannelAudioObserverEventHandler)
  .inSingletonScope();

container
  .bind<ChannelAudioObserverMissingProducerRestorer>(TOKEN.ChannelAudioObserverMissingProducerRestorer)
  .to(ChannelAudioObserverMissingProducerRestorer)
  .inSingletonScope();

container
  .bind<EventsQueue>(TOKEN.EventsQueue)
  .to(EventsQueue)
  .inSingletonScope();

container
  .bind<ChannelStateSynchronizer>(TOKEN.ChannelStateSynchronizer)
  .to(ChannelStateSynchronizer)
  .inSingletonScope();

container
  .bind<ChannelStateSyncEventHandler>(TOKEN.ChannelStateSyncEventHandler)
  .to(ChannelStateSyncEventHandler)
  .inSingletonScope();

container
  .bind<StatsHandler>(TOKEN.StatsHandler)
  .to(StatsHandler)
  .inSingletonScope();

container
  .bind<WIDHandler>(TOKEN.WIDHandler)
  .to(WIDHandler)
  .inSingletonScope();

container
  .bind<number[]>(TOKEN.WIDIgnoreSsrcList)
  .toConstantValue([MEDIASOUP_SSRC_PROBATOR]);

container
  .bind<CompositeStatsParser>(TOKEN.CompositeStatsParser)
  .toDynamicValue((context: interfaces.Context): CompositeStatsParser => {
    const loggerFactory = context.container.get<(namespace: string) => Logger>(TOKEN.LoggerFactory);
    const logger = loggerFactory('WID');
    const ignoreSSRCList = context.container.get<number[]>(TOKEN.WIDIgnoreSsrcList);

    return new CompositeRTCStatsParser({
      statsParser: new RTCStatsParser({
        ignoreSSRCList,
        logger,
      }),
    });
  })
  .inSingletonScope();

container
  .bind<PeriodicWebRTCStatsReporter>(TOKEN.PeriodicStatsReporter)
  .toDynamicValue((context: interfaces.Context): PeriodicWebRTCStatsReporter => {
    const config = context.container.get<ConfigService>(TOKEN.Config);
    const compositeStatsParser = context.container.get<CompositeStatsParser>(TOKEN.CompositeStatsParser);

    return new PeriodicWebRTCStatsReporter({
      compositeStatsParser,
      getStatsInterval: config.get('getStatsInterval'),
    });
  })
  .inSingletonScope();

container
  .bind<WebRTCIssueDetector | undefined>(TOKEN.WebRTCIssueDetector)
  .toDynamicValue((context: interfaces.Context): WebRTCIssueDetector | undefined => {
    const config = context.container.get<ConfigService>(TOKEN.Config);
    const widHandler = context.container.get<WIDHandler>(TOKEN.WIDHandler);
    const disableWid = config.get('disableWid');

    // If disableWID is true, return undefined
    if (disableWid) {
      return undefined;
    }

    const statsHandler = context.container.get<StatsHandler>(TOKEN.StatsHandler);

    const onIssues = (issues: IssueDetectorResult): void => {
      widHandler.handleIssues(issues);
      config.get('onIssues')?.(issues);
    };

    const onNetworkScoresUpdated = (networkScores: NetworkScores): void => {
      widHandler.handleNetworkScores(networkScores);
      config.get('onNetworkScoresUpdated')?.(networkScores);
    };

    const ignoreSSRCList = context.container.get<number[]>(TOKEN.WIDIgnoreSsrcList);

    const loggerFactory = context.container.get<(namespace: string) => Logger>(TOKEN.LoggerFactory);
    const logger = loggerFactory('WID');

    const compositeStatsParser = context.container.get<CompositeStatsParser>(TOKEN.CompositeStatsParser);
    const statsReporter = context.container.get<PeriodicWebRTCStatsReporter>(TOKEN.PeriodicStatsReporter);

    return new WebRTCIssueDetector({
      onIssues,
      issueEmitter: new WebRTCIssueEmitter(),
      networkScoresCalculator: new NetworkScoresCalculator(),
      detectors: [
        new QualityLimitationsIssueDetector(),
        new InboundNetworkIssueDetector(),
        new OutboundNetworkIssueDetector(),
        new NetworkMediaSyncIssueDetector(),
        new AvailableOutgoingBitrateIssueDetector(),
        new UnknownVideoDecoderImplementationDetector(),
        new FrozenVideoTrackDetector({
          minMosQuality: MosQuality.GOOD,
          avgFreezeDurationThresholdMs: 2_000,
          frozenDurationThresholdPct: 50,
        }),
        new VideoDecoderIssueDetector({
          minMosQuality: MosQuality.GOOD,
          volatilityThreshold: 10,
          affectedStreamsPercentThreshold: 60,
        }),
      ],
      statsReporter,
      onNetworkScoresUpdated,
      onStats: statsHandler.handle.bind(statsHandler),
      ignoreSSRCList,
      compositeStatsParser,
      logger,
      autoAddPeerConnections: false,
    });
  })
  .inSingletonScope();

container
  .bind<WIDStatsAdapter>(TOKEN.WIDStatsAdapter)
  .to(WIDStatsAdapter)
  .inSingletonScope();

container
  .bind<ScoresCalculator>(TOKEN.ScoresCalculator)
  .toDynamicValue(() => new ScoresCalculator())
  .inSingletonScope();

container
  .bind<Engine>(TOKEN.Engine)
  .to(Engine)
  .inSingletonScope();

// Factories

container
  .bind<PeerTrackFactory>(TOKEN.PeerTrackFactory)
  .toFactory(peerTrackFactory);

container
  .bind<PeerFactory>(TOKEN.PeerFactory)
  .toFactory(peerFactory);

container
  .bind<PeerConsumerFactory>(TOKEN.PeerConsumerFactory)
  .toFactory(peerConsumerFactory);

container
  .bind<AudioTrackFactory>(TOKEN.AudioTrackFactory)
  .toFactory(audioTrackFactory);

container
  .bind<VideoTrackFactory>(TOKEN.VideoTrackFactory)
  .toFactory(videoTrackFactory);

container
  .bind<AudioTrackProcessorFactory>(TOKEN.AudioTrackProcessorFactory)
  .toFactory(audioTrackProcessorFactory);

container
  .bind<DataChannelsManager>(TOKEN.DataChannelsManager)
  .to(DataChannelsManager)
  .inSingletonScope();

container
  .bind<DatachannelHeartbeater>(TOKEN.DatachannelHeartbeater)
  .to(DatachannelHeartbeater)
  .inSingletonScope();

container
  .bind<SignalingHeartbeater>(TOKEN.SignalingHeartbeater)
  .to(SignalingHeartbeater)
  .inSingletonScope();

container
  .bind<AnalyticsApiClient>(TOKEN.AnalyticsApiClient)
  .toDynamicValue((context: interfaces.Context) => {
    const config = context.container.get<ConfigService>(TOKEN.Config);

    return new AnalyticsApiClient(config.get('analyticsApiUrl'));
  })
  .inSingletonScope();

container
  .bind<PeersStorage>(TOKEN.PeersStorage)
  .to(PeersStorage)
  .inSingletonScope();

container
  .bind<TransportsStatsProvider>(TOKEN.TransportsStatsProvider)
  .to(TransportsStatsProvider)
  .inSingletonScope();
